import interstellarApi from './interstellarApi';

/** Módulo de llamadas (entrante) asociado a la cuenta */
export interface CallModuleSummary {
  id: number;
  name: string;
  phone_number: string;
  account_id?: number | null;
}

/** Config de ruta para un schedule_type (in_office, out_of_office, time_off) */
export interface AgentRouteConfig {
  call_module_id: number;
  schedule_type: 'in_office' | 'out_of_office' | 'time_off';
  config_type: 'agent_route';
  date?: string | null;
  group_id: number;
  group_order: number[];
  ring_strategy_id: number;
  call_resolution_strategy_id: number;
  greeting_playback_id: number;
  hold_music_playback_id: number;
  announce_playback_id: number;
  announce_frequency: number;
  max_wait_time: number;
  max_calls_on_wait: number;
}

export interface MessageRouteConfig {
  call_module_id: number;
  schedule_type: 'in_office' | 'out_of_office' | 'time_off';
  config_type: 'message_route';
  date?: string | null;
  message_playback_id: number;
}

export interface ExternalNumberRouteConfig {
  call_module_id: number;
  schedule_type: 'in_office' | 'out_of_office' | 'time_off';
  config_type: 'external_number_route';
  date?: string | null;
  external_phone_number: number;
}

export type CallModuleConfig = AgentRouteConfig | MessageRouteConfig | ExternalNumberRouteConfig;

/** Detalle completo de un módulo de llamada con sus configs */
export interface CallModuleDetail extends CallModuleSummary {
  schedule_id: number;
  configs: CallModuleConfig[];
  schedule?: {
    id: number;
    name: string;
  };
}

interface CallModulesListResponse {
  total_count: number;
  page: number;
  page_size: number;
  call_modules: CallModuleSummary[];
}

/** Teléfono disponible en el pool para asignar a un módulo */
export interface AvailablePhoneNumber {
  id: number;
  phone_number: string;
  created_at?: string | null;
}

interface PhoneNumberPoolResponse {
  total_count: number;
  page: number;
  page_size: number;
  phone_numbers: AvailablePhoneNumber[];
}

/** Payload para crear un módulo de llamada */
export interface CreateCallModulePayload {
  schedule_id: number;
  name: string;
  /** Número manual (ingreso directo) */
  phone_number?: string;
  /** ID del número a asignar del pool */
  phone_number_id?: number;
  configs: Array<{
    config_type: 'agent_route' | 'message_route' | 'external_number_route';
    data: {
      schedule_type: 'in_office' | 'out_of_office' | 'time_off';
      date?: string | null;
      // Agent route fields
      group_id?: number;
      group_order?: number[];
      ring_strategy_id?: number;
      call_resolution_strategy_id?: number;
      greeting_playback_id?: number;
      hold_music_playback_id?: number;
      announce_playback_id?: number;
      announce_frequency?: number;
      max_wait_time?: number;
      max_calls_on_wait?: number;
      // Message route fields
      message_playback_id?: number;
      // External number route fields
      external_phone_number?: number;
    };
  }>;
}

interface CreateCallModuleResponse {
  message: string;
  id: number;
}

/**
 * Lista módulos de llamada de la cuenta: `GET /incoming-call-config/module?account_id=&page=&page_size=`
 */
export const fetchCallModulesForAccount = async (
  accountId: number,
  pageSize = 200
): Promise<CallModuleSummary[]> => {
  const response = await interstellarApi.get<CallModulesListResponse>(
    '/incoming-call-config/module',
    {
      params: {
        account_id: accountId,
        page: 1,
        page_size: pageSize,
      },
    }
  );
  return response.data.call_modules ?? [];
};

/**
 * Obtiene detalle completo de un módulo: `GET /incoming-call-config/module/{moduleId}`
 */
export const fetchCallModule = async (moduleId: number): Promise<CallModuleDetail> => {
  const response = await interstellarApi.get<CallModuleDetail>(
    `/incoming-call-config/module/${moduleId}`
  );
  return response.data;
};

/**
 * Crea un nuevo módulo de llamada: `POST /incoming-call-config/module`
 */
export const createCallModule = async (
  accountId: number,
  payload: CreateCallModulePayload
): Promise<CreateCallModuleResponse> => {
  const response = await interstellarApi.post<CreateCallModuleResponse>(
    '/incoming-call-config/module',
    payload,
    { params: { account_id: accountId } }
  );
  return response.data;
};

/**
 * Actualiza un módulo existente: `PUT /incoming-call-config/module/{moduleId}`
 */
export const updateCallModule = async (
  moduleId: number,
  payload: CreateCallModulePayload
): Promise<{ message: string; id: number }> => {
  const response = await interstellarApi.put<{ message: string; id: number }>(
    `/incoming-call-config/module/${moduleId}`,
    payload
  );
  return response.data;
};

/**
 * Elimina un módulo: `DELETE /incoming-call-config/module/{moduleId}`
 */
export const deleteCallModule = async (moduleId: number): Promise<void> => {
  await interstellarApi.delete(`/incoming-call-config/module/${moduleId}`);
};

/**
 * Lista números telefónicos disponibles (no asignados) en el pool:
 * `GET /incoming-call-config/phone-number-pool`
 */
export const fetchAvailablePhoneNumbers = async (
  page = 1,
  pageSize = 200
): Promise<AvailablePhoneNumber[]> => {
  const response = await interstellarApi.get<PhoneNumberPoolResponse>(
    '/incoming-call-config/phone-number-pool',
    { params: { page, page_size: pageSize } }
  );
  return response.data.phone_numbers ?? [];
};
